import React from 'react';
import { MonthlyTotals, MONTH_NAMES, getMonthValue, createEmptyMonthlyTotals } from '../../types';
import {
  DepartmentTemplate,
  ExpenseCategory,
  AccountDefinition,
  resolveAccountsForDepartment,
} from '../../config/docTemplate';
import { UnitCountsData } from '../../services/api';
import { ROW_COLORS, CATEGORY_COLORS, INCOME_CATEGORIES } from './constants';
import { addMonthlyTotals, subtractMonthlyTotals, applySign, formatPercent, formatPerUnit } from './calculations';
import { AccountData, DOCGrossData } from './types';

// ============================================================
// Helper types for calculation results
// ============================================================

interface TotalsResult {
  ytd: number;
  monthly: MonthlyTotals;
}

export interface DepartmentTotals {
  sales: TotalsResult;
  frontGross: TotalsResult;
  fiIncome: TotalsResult;
  totalGross: TotalsResult;
  totalExpenses: TotalsResult;
  operatingProfit: TotalsResult;
  rentalIncome: TotalsResult;
  rentalSellingExpense: TotalsResult;
  rentalSellingGross: TotalsResult;
  rentalFixedExpense: TotalsResult;
}

// ============================================================
// Calculation functions (depend on account data maps)
// ============================================================

export function getAccountWithSign(
  def: AccountDefinition,
  categoryName: string,
  accountDataMap: Map<string, AccountData>
): { ytd: number; monthly: MonthlyTotals } | null {
  const data = accountDataMap.get(def.code);
  if (!data) return null;

  const sign = def.sign || 'debit';
  const isIncomeCategory = INCOME_CATEGORIES.includes(categoryName);
  const ytd = applySign(data.ytdTotal, sign, isIncomeCategory);
  const monthly: MonthlyTotals = {
    month1: applySign(getMonthValue(data.monthlyTotals, 1), sign, isIncomeCategory),
    month2: applySign(getMonthValue(data.monthlyTotals, 2), sign, isIncomeCategory),
    month3: applySign(getMonthValue(data.monthlyTotals, 3), sign, isIncomeCategory),
    month4: applySign(getMonthValue(data.monthlyTotals, 4), sign, isIncomeCategory),
    month5: applySign(getMonthValue(data.monthlyTotals, 5), sign, isIncomeCategory),
    month6: applySign(getMonthValue(data.monthlyTotals, 6), sign, isIncomeCategory),
    month7: applySign(getMonthValue(data.monthlyTotals, 7), sign, isIncomeCategory),
    month8: applySign(getMonthValue(data.monthlyTotals, 8), sign, isIncomeCategory),
    month9: applySign(getMonthValue(data.monthlyTotals, 9), sign, isIncomeCategory),
    month10: applySign(getMonthValue(data.monthlyTotals, 10), sign, isIncomeCategory),
    month11: applySign(getMonthValue(data.monthlyTotals, 11), sign, isIncomeCategory),
    month12: applySign(getMonthValue(data.monthlyTotals, 12), sign, isIncomeCategory),
  };

  return { ytd, monthly };
}

export function calculateCategoryTotals(
  category: ExpenseCategory,
  accountDataMap: Map<string, AccountData>
): TotalsResult {
  let ytd = 0;
  let monthly = createEmptyMonthlyTotals();

  category.accounts.forEach(acc => {
    const data = getAccountWithSign(acc, category.name, accountDataMap);
    if (data) {
      ytd += data.ytd;
      monthly = addMonthlyTotals(monthly, data.monthly);
    }
  });

  return { ytd, monthly };
}

export function calculateDepartmentTotals(
  dept: DepartmentTemplate,
  accountDataMap: Map<string, AccountData>,
  docGrossMap: Map<string, DOCGrossData>
): DepartmentTotals {
  let salesYtd = 0;
  let salesMonthly = createEmptyMonthlyTotals();
  let frontGrossYtd = 0;
  let frontGrossMonthly = createEmptyMonthlyTotals();
  let fiYtd = 0;
  let fiMonthly = createEmptyMonthlyTotals();
  let totalGrossYtd = 0;
  let totalGrossMonthly = createEmptyMonthlyTotals();
  let rentalIncomeYtd = 0;
  let rentalIncomeMonthly = createEmptyMonthlyTotals();

  const deptToFrontGross: Record<string, string> = {
    'NEW_VEHICLE': 'NEW_VEHICLE',
    'USED_VEHICLE': 'USED_VEHICLE',
    'SERVICE': 'SERVICE',
    'PARTS': 'PARTS',
    'BODY_SHOP': 'BODY_SHOP',
    'OTHERS': 'OTHERS',
  };

  const deptToFI: Record<string, string> = {
    'NEW_VEHICLE': 'F_AND_I_NEW',
    'USED_VEHICLE': 'F_AND_I_USED',
  };

  const frontGrossDept = deptToFrontGross[dept.name];
  if (frontGrossDept) {
    const dg = docGrossMap.get(frontGrossDept);
    if (dg) {
      frontGrossYtd = dg.ytdGross;
      frontGrossMonthly = dg.gross || dg.monthlyGross || createEmptyMonthlyTotals();
      if (dg.ytdSales !== undefined) {
        salesYtd = dg.ytdSales;
      }
      if (dg.sales) {
        salesMonthly = dg.sales;
      }
    }
  }

  const fiDept = deptToFI[dept.name];
  if (fiDept) {
    const fi = docGrossMap.get(fiDept);
    if (fi) {
      fiYtd = fi.ytdGross;
      fiMonthly = fi.gross || fi.monthlyGross || createEmptyMonthlyTotals();
    }
  }

  totalGrossYtd = frontGrossYtd + fiYtd;
  totalGrossMonthly = addMonthlyTotals(frontGrossMonthly, fiMonthly);

  let rentalSellingExpenseYtd = 0;
  let rentalSellingExpenseMonthly = createEmptyMonthlyTotals();
  let rentalSellingGrossYtd = 0;
  let rentalSellingGrossMonthly = createEmptyMonthlyTotals();
  let rentalFixedExpenseYtd = 0;
  let rentalFixedExpenseMonthly = createEmptyMonthlyTotals();

  if (dept.name === 'RENTAL') {
    const rentalIncomeCategory = dept.expenseCategories.find(c => c.name === 'RENTAL_INCOME');
    if (rentalIncomeCategory) {
      const totals = calculateCategoryTotals(rentalIncomeCategory, accountDataMap);
      rentalIncomeYtd = totals.ytd;
      rentalIncomeMonthly = totals.monthly;
    }

    const rentalSellingCategory = dept.expenseCategories.find(c => c.name === 'RENTAL_SELLING');
    if (rentalSellingCategory) {
      const totals = calculateCategoryTotals(rentalSellingCategory, accountDataMap);
      rentalSellingExpenseYtd = totals.ytd;
      rentalSellingExpenseMonthly = totals.monthly;
    }

    const rentalFixedCategory = dept.expenseCategories.find(c => c.name === 'RENTAL_FIXED');
    if (rentalFixedCategory) {
      const totals = calculateCategoryTotals(rentalFixedCategory, accountDataMap);
      rentalFixedExpenseYtd = totals.ytd;
      rentalFixedExpenseMonthly = totals.monthly;
    }

    rentalSellingGrossYtd = rentalIncomeYtd - rentalSellingExpenseYtd;
    rentalSellingGrossMonthly = subtractMonthlyTotals(rentalIncomeMonthly, rentalSellingExpenseMonthly);
  }

  let expenseYtd = 0;
  let expenseMonthly = createEmptyMonthlyTotals();

  dept.expenseCategories.forEach(cat => {
    if (cat.name === 'RENTAL_INCOME') return;
    const totals = calculateCategoryTotals(cat, accountDataMap);
    expenseYtd += totals.ytd;
    expenseMonthly = addMonthlyTotals(expenseMonthly, totals.monthly);
  });

  let profitYtd = 0;
  let profitMonthly = createEmptyMonthlyTotals();

  if (dept.name === 'RENTAL') {
    profitYtd = rentalIncomeYtd - expenseYtd;
    profitMonthly = subtractMonthlyTotals(rentalIncomeMonthly, expenseMonthly);
  } else if (dept.name === 'MISC') {
    profitYtd = -expenseYtd;
    profitMonthly = {
      month1: -expenseMonthly.month1,
      month2: -expenseMonthly.month2,
      month3: -expenseMonthly.month3,
      month4: -expenseMonthly.month4,
      month5: -expenseMonthly.month5,
      month6: -expenseMonthly.month6,
      month7: -expenseMonthly.month7,
      month8: -expenseMonthly.month8,
      month9: -expenseMonthly.month9,
      month10: -expenseMonthly.month10,
      month11: -expenseMonthly.month11,
      month12: -expenseMonthly.month12,
    };
  } else {
    profitYtd = totalGrossYtd - expenseYtd;
    profitMonthly = subtractMonthlyTotals(totalGrossMonthly, expenseMonthly);
  }

  return {
    sales: { ytd: salesYtd, monthly: salesMonthly },
    frontGross: { ytd: frontGrossYtd, monthly: frontGrossMonthly },
    fiIncome: { ytd: fiYtd, monthly: fiMonthly },
    totalGross: { ytd: totalGrossYtd, monthly: totalGrossMonthly },
    totalExpenses: { ytd: expenseYtd, monthly: expenseMonthly },
    operatingProfit: { ytd: profitYtd, monthly: profitMonthly },
    rentalIncome: { ytd: rentalIncomeYtd, monthly: rentalIncomeMonthly },
    rentalSellingExpense: { ytd: rentalSellingExpenseYtd, monthly: rentalSellingExpenseMonthly },
    rentalSellingGross: { ytd: rentalSellingGrossYtd, monthly: rentalSellingGrossMonthly },
    rentalFixedExpense: { ytd: rentalFixedExpenseYtd, monthly: rentalFixedExpenseMonthly },
  };
}

// ============================================================
// Render functions
// ============================================================

export function renderDataRow(
  label: string,
  ytd: number,
  monthly: MonthlyTotals,
  colorClass: string,
  formatCurrency: (value: number) => string,
  isBold: boolean = false,
  indent: number = 0,
  showPercentColumn: boolean = false,
  percentValue?: number,
  percentGross?: number,
  rightColumnValue?: string
) {
  return (
    <tr className={`${colorClass} border-b border-gray-200`}>
      <td
        className={`min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-xs lg:text-sm ${isBold ? 'font-semibold' : ''} sticky lg:static left-0 ${colorClass}`}
        style={{ paddingLeft: `${8 + indent * 16}px` }}
      >
        {label}
      </td>
      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
        <td
          key={month}
          className={`min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right text-xs ${isBold ? 'font-semibold' : ''}`}
        >
          {formatCurrency(getMonthValue(monthly, month))}
        </td>
      ))}
      <td className={`min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm ${isBold ? 'font-bold' : 'font-semibold'} bg-gray-100`}>
        {formatCurrency(ytd)}
      </td>
      {showPercentColumn && (
        <td className={`min-w-[60px] lg:min-w-0 lg:w-[5%] px-1 py-2 text-right text-xs ${isBold ? 'font-bold' : 'font-semibold'} bg-gray-100`}>
          {rightColumnValue !== undefined ? rightColumnValue : (percentValue !== undefined && percentGross !== undefined ? formatPercent(percentValue, percentGross) : '')}
        </td>
      )}
    </tr>
  );
}

export function renderAccountRow(
  def: AccountDefinition,
  data: { ytd: number; monthly: MonthlyTotals },
  formatCurrency: (value: number) => string,
  departmentGross?: number
) {
  return (
    <tr key={def.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
      <td className="min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-1.5 text-xs sticky lg:static left-0 bg-gray-50" style={{ paddingLeft: '40px' }}>
        <div className="flex flex-col lg:flex-row lg:items-center">
          <span className="font-mono text-gray-600 mr-2 flex-shrink-0">{def.code}</span>
          <span className="text-gray-500 truncate text-xs">{def.description}</span>
        </div>
      </td>
      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
        <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-1.5 text-right text-xs text-gray-700">
          {formatCurrency(getMonthValue(data.monthly, month))}
        </td>
      ))}
      <td className="min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-1.5 text-right text-xs font-medium bg-gray-100">
        {formatCurrency(data.ytd)}
      </td>
      {departmentGross !== undefined && (
        <td className="min-w-[60px] lg:min-w-0 lg:w-[5%] px-1 py-1.5 text-right text-xs text-gray-600 bg-gray-50">
          {formatPercent(data.ytd, departmentGross)}
        </td>
      )}
    </tr>
  );
}

export function renderExpenseCategory(
  dept: DepartmentTemplate,
  category: ExpenseCategory,
  accountDataMap: Map<string, AccountData>,
  expandedCategories: Set<string>,
  toggleCategory: (key: string) => void,
  formatCurrency: (value: number) => string,
  departmentGross?: number,
  showPercentColumn: boolean = true
) {
  const categoryKey = `${dept.name}-${category.name}`;
  const isExpanded = expandedCategories.has(categoryKey);
  const totals = calculateCategoryTotals(category, accountDataMap);
  const colorClass = CATEGORY_COLORS[category.name] || ROW_COLORS.otherExpense;

  const accountsWithData = category.accounts
    .map(acc => ({ def: acc, data: getAccountWithSign(acc, category.name, accountDataMap) }))
    .filter(item => item.data !== null);

  if (totals.ytd === 0 && accountsWithData.length === 0) {
    return null;
  }

  const showPercentValue = departmentGross !== undefined &&
    !INCOME_CATEGORIES.includes(category.name) &&
    category.name !== 'RENTAL_INCOME';

  return (
    <React.Fragment key={categoryKey}>
      <tr
        className={`${colorClass} border-b border-gray-200 ${accountsWithData.length > 0 ? 'cursor-pointer hover:opacity-90' : ''}`}
        onClick={() => accountsWithData.length > 0 && toggleCategory(categoryKey)}
      >
        <td className={`min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-xs lg:text-sm sticky lg:static left-0 ${colorClass}`} style={{ paddingLeft: '24px' }}>
          <div className="flex items-center">
            {accountsWithData.length > 0 && (
              <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            {accountsWithData.length === 0 && <span className="mr-2 w-4 flex-shrink-0"></span>}
            <span>{category.displayName}</span>
            {accountsWithData.length > 0 && (
              <span className="ml-2 text-xs text-gray-500">({accountsWithData.length})</span>
            )}
          </div>
        </td>
        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
          <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right text-xs">
            {formatCurrency(getMonthValue(totals.monthly, month))}
          </td>
        ))}
        <td className="min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-semibold bg-gray-100">
          {formatCurrency(totals.ytd)}
        </td>
        {showPercentColumn && (
          <td className="min-w-[60px] lg:min-w-0 lg:w-[5%] px-1 py-2 text-right text-xs font-semibold bg-gray-100">
            {showPercentValue && departmentGross !== undefined ? formatPercent(totals.ytd, departmentGross) : ''}
          </td>
        )}
      </tr>

      {isExpanded && accountsWithData.map(item =>
        renderAccountRow(item.def, item.data!, formatCurrency, showPercentValue ? departmentGross : undefined)
      )}
    </React.Fragment>
  );
}

export function renderUnitCounts(
  deptName: string,
  unitCountsData: UnitCountsData | null,
  formatCurrency: (value: number) => string
) {
  if (!unitCountsData) return null;

  const isNewVehicle = deptName === 'NEW_VEHICLE';
  const isUsedVehicle = deptName === 'USED_VEHICLE';

  if (!isNewVehicle && !isUsedVehicle) return null;

  const units = isNewVehicle ? unitCountsData.newVehicleUnits : unitCountsData.usedVehicleUnits;
  const monthlyByBrand = isNewVehicle ? unitCountsData.monthlyNewUnitsByBrand : unitCountsData.monthlyUsedUnitsByBrand;
  const monthlyTotal = isNewVehicle ? unitCountsData.monthlyNewUnits : unitCountsData.monthlyUsedUnits;
  const totalUnits = isNewVehicle ? unitCountsData.totalNewUnits : unitCountsData.totalUsedUnits;
  const colorClass = isNewVehicle ? 'bg-blue-50 text-blue-900' : 'bg-amber-50 text-amber-900';
  const headerColorClass = isNewVehicle ? 'bg-blue-100 text-blue-900' : 'bg-amber-100 text-amber-900';
  const totalColorClass = isNewVehicle ? 'bg-blue-200' : 'bg-amber-200';

  if (Object.keys(units).length === 0) return null;

  return (
    <>
      <tr className={headerColorClass}>
        <td colSpan={15} className={`px-2 lg:px-3 py-2 text-xs lg:text-sm font-bold ${headerColorClass}`}>
          Units Sold by Brand
        </td>
      </tr>
      {Object.entries(units)
        .sort(([, a], [, b]) => b - a)
        .map(([brand, ytdCount]) => {
          const monthlyData = monthlyByBrand?.[brand];
          return (
            <tr key={`unit-${brand}`} className={`${colorClass} border-b border-gray-100`}>
              <td className={`min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-1.5 text-xs sticky lg:static left-0 ${colorClass}`} style={{ paddingLeft: '24px' }}>
                {brand}
              </td>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-1.5 text-right text-xs">
                  {monthlyData ? getMonthValue(monthlyData, month) || '--' : '--'}
                </td>
              ))}
              <td className={`min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-1.5 text-right text-xs font-semibold ${headerColorClass}`}>
                {ytdCount}
              </td>
              <td className="min-w-[60px] lg:min-w-0 lg:w-[5%] px-1 py-1.5 text-right text-xs"></td>
            </tr>
          );
        })}
      <tr className={headerColorClass}>
        <td className={`min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-bold sticky lg:static left-0 ${headerColorClass}`}>
          Total Units
        </td>
        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
          const monthTotal = monthlyTotal ? getMonthValue(monthlyTotal, month) : 0;
          return (
            <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right text-xs font-semibold">
              {monthTotal || '--'}
            </td>
          );
        })}
        <td className={`min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold ${totalColorClass}`}>
          {totalUnits}
        </td>
        <td className="min-w-[60px] lg:min-w-0 lg:w-[5%] px-1 py-2 text-right text-xs"></td>
      </tr>
    </>
  );
}

export function renderDepartment(
  dept: DepartmentTemplate,
  locationTotalGross: number,
  accountDataMap: Map<string, AccountData>,
  docGrossMap: Map<string, DOCGrossData>,
  unitCountsData: UnitCountsData | null,
  expandedDepartments: Set<string>,
  expandedCategories: Set<string>,
  toggleDepartment: (name: string) => void,
  toggleCategory: (key: string) => void,
  formatCurrency: (value: number) => string
) {
  // Resolve suffix-based pattern matching (discovers new Body Shop accounts from available data)
  const resolvedCategories = resolveAccountsForDepartment(dept, Array.from(accountDataMap.keys()));
  const resolvedDept: DepartmentTemplate = resolvedCategories !== dept.expenseCategories
    ? { ...dept, expenseCategories: resolvedCategories }
    : dept;

  const isExpanded = expandedDepartments.has(dept.name);
  const totals = calculateDepartmentTotals(resolvedDept, accountDataMap, docGrossMap);
  const profitColor = totals.operatingProfit.ytd >= 0 ? 'text-green-600' : 'text-red-600';

  const getUnitCountForHeader = () => {
    if (!unitCountsData) return null;
    if (dept.name === 'NEW_VEHICLE') return unitCountsData.totalNewUnits;
    if (dept.name === 'USED_VEHICLE') return unitCountsData.totalUsedUnits;
    return null;
  };
  const headerUnitCount = getUnitCountForHeader();

  return (
    <div key={dept.name} className="bg-white rounded-lg shadow mb-4">
      <div
        className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
        onClick={() => toggleDepartment(dept.name)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2 md:space-x-3">
            <span className="text-base md:text-lg text-gray-400">
              {isExpanded ? '▼' : '▶'}
            </span>
            <h3 className="text-base md:text-lg font-semibold text-gray-900">
              {dept.name === 'MISC' ? dept.displayName : `${dept.displayName} Department`}
              {headerUnitCount !== null && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({headerUnitCount} units)
                </span>
              )}
            </h3>
          </div>
          <div className="text-right">
            <div className={`text-base md:text-lg font-bold ${dept.name === 'MISC' ? 'text-gray-700' : dept.name === 'OTHERS' ? 'text-green-600' : profitColor}`}>
              {formatCurrency(
                dept.name === 'MISC' ? totals.totalExpenses.ytd :
                dept.name === 'OTHERS' ? totals.totalGross.ytd :
                totals.operatingProfit.ytd
              )}
              {dept.name !== 'MISC' && locationTotalGross !== 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({formatPercent(
                    dept.name === 'OTHERS' ? totals.totalGross.ytd :
                    dept.name === 'RENTAL' ? totals.operatingProfit.ytd :
                    totals.operatingProfit.ytd,
                    locationTotalGross
                  )})
                </span>
              )}
            </div>
            {dept.name !== 'MISC' && dept.name !== 'OTHERS' && (
              <div className="text-xs text-gray-500">
                Gross: {formatCurrency(totals.totalGross.ytd)} | Exp: {formatCurrency(totals.totalExpenses.ytd)}
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-700 text-white text-xs">
                <th className="min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-left font-semibold sticky lg:static left-0 bg-gray-700 z-20">
                  Account / Category
                </th>
                {MONTH_NAMES.map(month => (
                  <th key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right font-semibold">
                    {month}
                  </th>
                ))}
                <th className="min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right font-bold bg-gray-800">
                  YTD
                </th>
                {dept.name !== 'OTHERS' && (
                  <th className="min-w-[60px] lg:min-w-0 lg:w-[5%] px-1 py-2 text-right font-semibold bg-gray-800">
                    % Gross
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {renderUnitCounts(dept.name, unitCountsData, formatCurrency)}

              {/* NEW/USED DEPARTMENTS: Sales, Gross (Frontend), F&I (Backend), Total Gross */}
              {dept.hasFandI && (() => {
                const deptUnits = dept.name === 'NEW_VEHICLE'
                  ? (unitCountsData?.totalNewUnits || 0)
                  : (unitCountsData?.totalUsedUnits || 0);
                return (
                  <>
                    {renderDataRow('Sales YTD', totals.sales.ytd, totals.sales.monthly, ROW_COLORS.sales, formatCurrency, true, 0, true)}
                    {renderDataRow('Gross (Frontend) YTD', totals.frontGross.ytd, totals.frontGross.monthly, ROW_COLORS.gross, formatCurrency, true, 0, true, undefined, undefined, formatPerUnit(totals.frontGross.ytd, deptUnits, formatCurrency))}
                    {renderDataRow('F&I (Backend) YTD', totals.fiIncome.ytd, totals.fiIncome.monthly, ROW_COLORS.fiIncome, formatCurrency, true, 0, true, undefined, undefined, formatPerUnit(totals.fiIncome.ytd, deptUnits, formatCurrency))}
                    {renderDataRow('Total Gross', totals.totalGross.ytd, totals.totalGross.monthly, ROW_COLORS.totalGross, formatCurrency, true, 0, true)}
                  </>
                );
              })()}

              {/* SERVICE/PARTS/BODY SHOP DEPARTMENTS: Sales, Gross */}
              {dept.hasRevenue && !dept.hasFandI && (
                <>
                  {renderDataRow('Sales YTD', totals.sales.ytd, totals.sales.monthly, ROW_COLORS.sales, formatCurrency, true, 0, true)}
                  {renderDataRow('Gross YTD', totals.frontGross.ytd, totals.frontGross.monthly, ROW_COLORS.gross, formatCurrency, true, 0, true)}
                </>
              )}

              {/* RENTAL DEPARTMENT: Special structure */}
              {resolvedDept.name === 'RENTAL' && (
                <>
                  {resolvedDept.expenseCategories
                    .filter(cat => cat.name === 'RENTAL_INCOME')
                    .map(cat => renderExpenseCategory(resolvedDept, cat, accountDataMap, expandedCategories, toggleCategory, formatCurrency))}
                </>
              )}

              {/* OTHERS DEPARTMENT: Others Total */}
              {resolvedDept.name === 'OTHERS' && (
                renderDataRow('Others Total', totals.totalGross.ytd, totals.totalGross.monthly, ROW_COLORS.totalGross, formatCurrency, true)
              )}

              {/* RENTAL DEPARTMENT: Special expense structure */}
              {resolvedDept.name === 'RENTAL' && (
                <>
                  {resolvedDept.expenseCategories
                    .filter(cat => cat.name === 'RENTAL_SELLING')
                    .map(cat => renderExpenseCategory(resolvedDept, cat, accountDataMap, expandedCategories, toggleCategory, formatCurrency, totals.rentalIncome.ytd))}
                  {renderDataRow('Rental Selling Expense Total', totals.rentalSellingExpense.ytd, totals.rentalSellingExpense.monthly, ROW_COLORS.totalExpense, formatCurrency, true, 0, true, totals.rentalSellingExpense.ytd, totals.rentalIncome.ytd)}
                  {renderDataRow('Rental Selling Gross Total', totals.rentalSellingGross.ytd, totals.rentalSellingGross.monthly, totals.rentalSellingGross.ytd >= 0 ? ROW_COLORS.gross : ROW_COLORS.loss, formatCurrency, true, 0, true)}
                  {resolvedDept.expenseCategories
                    .filter(cat => cat.name === 'RENTAL_FIXED')
                    .map(cat => renderExpenseCategory(resolvedDept, cat, accountDataMap, expandedCategories, toggleCategory, formatCurrency, totals.rentalIncome.ytd))}
                  {renderDataRow('Rental Fixed Expense Total', totals.rentalFixedExpense.ytd, totals.rentalFixedExpense.monthly, ROW_COLORS.fixedExpense, formatCurrency, true, 0, true, totals.rentalFixedExpense.ytd, totals.rentalIncome.ytd)}
                  {renderDataRow('Total Expenses', totals.totalExpenses.ytd, totals.totalExpenses.monthly, ROW_COLORS.totalExpense, formatCurrency, true, 0, true, totals.totalExpenses.ytd, totals.rentalIncome.ytd)}
                  {renderDataRow('Rental Operating Profit', totals.operatingProfit.ytd, totals.operatingProfit.monthly, totals.operatingProfit.ytd >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss, formatCurrency, true, 0, true)}
                </>
              )}

              {/* NON-RENTAL DEPARTMENTS: Standard expense categories (uses resolved categories for suffix-based discovery) */}
              {resolvedDept.name !== 'RENTAL' && resolvedDept.expenseCategories
                .filter(cat => cat.name !== 'RENTAL_INCOME' && (resolvedDept.name !== 'OTHERS' || cat.name !== 'OTHER_INCOME'))
                .map(cat => renderExpenseCategory(resolvedDept, cat, accountDataMap, expandedCategories, toggleCategory, formatCurrency, resolvedDept.name !== 'OTHERS' ? totals.totalGross.ytd : undefined))}

              {/* OTHERS department expandable account details */}
              {resolvedDept.name === 'OTHERS' && resolvedDept.expenseCategories
                .filter(cat => cat.name === 'OTHER_INCOME')
                .map(cat => renderExpenseCategory(resolvedDept, cat, accountDataMap, expandedCategories, toggleCategory, formatCurrency, undefined, false))}

              {/* Total Expenses - shown for all except MISC, OTHERS, and RENTAL */}
              {dept.name !== 'MISC' && dept.name !== 'OTHERS' && dept.name !== 'RENTAL' && renderDataRow(
                'Total Expenses', totals.totalExpenses.ytd, totals.totalExpenses.monthly, ROW_COLORS.totalExpense, formatCurrency, true, 0, true, totals.totalExpenses.ytd, totals.totalGross.ytd
              )}

              {/* MISC: Total Misc | OTHERS: nothing | RENTAL: already shown | Others: Operating Profit */}
              {dept.name === 'MISC' ? (
                renderDataRow('Total Misc', totals.totalExpenses.ytd, totals.totalExpenses.monthly, ROW_COLORS.totalMisc, formatCurrency, true, 0, true)
              ) : dept.name === 'OTHERS' ? (
                null
              ) : dept.name === 'RENTAL' ? (
                null
              ) : (
                renderDataRow('Operating Profit', totals.operatingProfit.ytd, totals.operatingProfit.monthly, totals.operatingProfit.ytd >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss, formatCurrency, true, 0, true)
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
